import {useContext} from 'react'
import {useDispatch} from 'react-redux'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {
  removeOnAuthSuccessLandingUrl,
  setOnAuthSuccessLandingUrl
} from '@vio/core/src/auth/business/persistData'
import {
  setUnlockHotelId,
  showPasswordlessLogin,
  SusiHistoryState
} from '@vio/core/src/auth/modules/slice'
import {
  AuthenticationFlows,
  ShowPasswordlessLoginPayload
} from '@vio/core/src/auth/types/Auth'

import {SusiContext} from '../SUSI/bottomSheet/SusiBottomSheet/SusiProvider'

type UseLogInParamsType = Pick<
  ShowPasswordlessLoginPayload,
  | 'flow'
  | 'anonymousId'
  | 'unlockHotelId'
  | 'analyticsData'
  | 'onAuthSuccessLandingUrl'
> & {sourceComponent?: string}

type HandleLogInParamsType = UseLogInParamsType & {
  isMobile: boolean
  updateSUSILocationState?: (state: SusiHistoryState) => void
}

const handleLogIn = ({
  anonymousId,
  flow,
  isMobile,
  sourceComponent,
  unlockHotelId,
  updateSUSILocationState,
  analyticsData,
  onAuthSuccessLandingUrl
}: HandleLogInParamsType) => {
  const authFlow = flow

  const isBottomSheetLoginVisible = isMobile
  const isPasswordlessLoginVisible = !isBottomSheetLoginVisible

  if (isBottomSheetLoginVisible && updateSUSILocationState) {
    updateSUSILocationState({active: true, analyticsData})
  }

  if (onAuthSuccessLandingUrl) {
    setOnAuthSuccessLandingUrl(onAuthSuccessLandingUrl)
  } else {
    removeOnAuthSuccessLandingUrl()
  }

  return showPasswordlessLogin({
    anonymousId,
    flow: authFlow || AuthenticationFlows.Login,
    isPasswordlessLoginVisible,
    sourceComponent,
    unlockHotelId
  })
}

export const useLogIn = () => {
  const dispatch = useDispatch()
  const {isMobile} = useDeviceLayout()
  const {updateSUSILocationState, trackSusiDisplayed} = useContext(SusiContext)

  return ({
    anonymousId,
    flow,
    sourceComponent,
    unlockHotelId,
    analyticsData,
    onAuthSuccessLandingUrl
  }: UseLogInParamsType) => {
    dispatch(setUnlockHotelId(unlockHotelId))
    dispatch(
      handleLogIn({
        anonymousId,
        flow,
        isMobile,
        sourceComponent,
        unlockHotelId,
        analyticsData,
        onAuthSuccessLandingUrl,
        ...(!!updateSUSILocationState && {updateSUSILocationState})
      })
    )
    // Last so analyticsData cannot replace it, conditional so an absent one
    // stays absent rather than becoming undefined.
    if (trackSusiDisplayed)
      trackSusiDisplayed(flow, {
        ...analyticsData,
        ...(!!sourceComponent && {sourceComponent})
      })
  }
}
